
// Chakra imports
import { Badge, Box, Button, Code, DrawerBody, DrawerFooter, Icon, Link, SimpleGrid, Spinner, Stack, Tooltip, useDisclosure } from "@chakra-ui/react";
import React, { useContext, useMemo, useState } from "react";
import { useContent } from "hooks/useContent";
import { AuthContext } from "contexts/AuthContext";
import { useDevice } from "hooks/useDevice";
import { MdShareLocation } from "react-icons/md";
import { ActionDrawer } from "components/drawer";
import DeviceModal from "views/modal/device";
import { ConfirmationDialog } from "components/confirmation/confirmation";
import { remove } from "lib/firebase";
import LocationModal from "views/modal/location";
import DataTableNew from "components/table/DataTableNew";
import { columnsDevices } from "./columns/devices";
import MultiSelectFilter from "components/filter/MultiSelectFilter";
import { androidVersionKey, appVersionKey, filterOptions, matchesFilter, modelKey } from "lib/devices";

export default function DevicesPage() {
  const { user } = useContext(AuthContext);
  const [data, loading, error, refresh] = useDevice({ user });

  // Chosen values of each filter; an empty list means "any"
  const [filter, setFilter] = useState({
    locationIds: [],
    appVersions: [],
    models: [],
    androidVersions: []
  })

  // The choices of the filters come from the devices themselves, with how many have each value
  const appVersionOptions = useMemo(() => filterOptions(data, appVersionKey), [data])
  const modelOptions = useMemo(() => filterOptions(data, modelKey), [data])
  const androidOptions = useMemo(() => filterOptions(data, androidVersionKey), [data])

  const locations = data.reduce((s, dv) => {
    const loc = dv.location || null
    if (!loc) return s
    if (s.find(o => o.id === loc.id)) return s
    s.push(loc)
    return s
  }, [])

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} flex="1" minH={0} display="flex" flexDirection="column">
      <DataTableNew
        loading={loading}
        columns={columnsDevices}
        data={data.filter(dv => {
          if (filter.locationIds.length > 0 && !filter.locationIds.includes(dv.locationId)) return false
          if (!matchesFilter(dv, appVersionKey, filter.appVersions)) return false
          if (!matchesFilter(dv, modelKey, filter.models)) return false
          if (!matchesFilter(dv, androidVersionKey, filter.androidVersions)) return false
          return true
        })}
        totalCount={data.length}
        refresh={refresh}
        resetPageKey={filter}
        defaultSort={[{ id: 'location', desc: false }]}
        filters={(
          <>
          <MultiSelectFilter label='Any location'
            options={locations.map(loc => ({ value: loc.id, label: loc.name }))}
            value={filter.locationIds}
            onChange={locationIds => setFilter({ ...filter, locationIds })} />
          <MultiSelectFilter label='Any app version' width='170px'
            options={appVersionOptions}
            value={filter.appVersions}
            onChange={appVersions => setFilter({ ...filter, appVersions })} />
          <MultiSelectFilter label='Any model' width='170px'
            options={modelOptions}
            value={filter.models}
            onChange={models => setFilter({ ...filter, models })} />
          <MultiSelectFilter label='Any Android' width='150px'
            options={androidOptions}
            value={filter.androidVersions}
            onChange={androidVersions => setFilter({ ...filter, androidVersions })} />
          </>
        )}
        actions={(
          <ActionDrawer
            title='Create device'
            trigger={
              <Button colorScheme='brand' variant='solid' size='sm' borderRadius={30} px='8'>
                New device
              </Button>
            }
            >
            <DeviceModal onChange={refresh} />
          </ActionDrawer>
        )}
      />
    </Box>
  );
}
